import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";

import { usePrivateAxios } from "@/api/use-private-axios";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarMinimal from "@/components/index/navbar-minimal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";

interface OrderSummaryData {
	buyerAddress: {
		address: string;
		city: string;
		state: string;
	};
	vendorAddress: {
		city: string;
		state: string;
	};
	items: Array<{
		id: string;
		productId: string;
		productName: string;
		productPhotos: string[];
		quantity: number;
		unitPrice: number;
		totalPrice: number;
	}>;
	contract: {
		id: string;
		contractName: string;
		qrCode: string;
		pickupLocation: string;
		shipOutDate: string;
	};
	pricing: {
		orderAmount: number;
		estimatedCourierFee: number;
		transactionFee: number;
		providerFee: number;
		totalAmount: number;
	};
}

function OrderSummaryPage() {
	const navigate = useNavigate();
	const search = useSearch({ from: "/checkout/summary" });
	const contractCodeFromSearch = search.contractCode || "";

	const [orderData, setOrderData] = useState<OrderSummaryData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [couponCode, setCouponCode] = useState("");
	const [courierPickupOfficeId, setCourierPickupOfficeId] = useState(
		() => localStorage.getItem("courierPickupOfficeId") || ""
	);
	const privateAxios = usePrivateAxios();

	useEffect(() => {
		const processOrder = async () => {
			try {
				let contractCode = contractCodeFromSearch;

				// If no contract code in URL, check for pending shipping data (coming from shipping page)
				if (!contractCode) {
					const pendingData = sessionStorage.getItem("pendingShippingData");
					if (!pendingData) {
						toast.error("No order data found");
						navigate({ to: "/bag" });
						return;
					}

					const shippingData = JSON.parse(pendingData);
					sessionStorage.removeItem("pendingShippingData");

					// Call buy-now API
					const response = await privateAxios.post("/bag/buy-now", {
						bagId: shippingData.bagId,
						shippingAddress: shippingData.shippingAddress
					});

					console.log("Buy-now response:", response.data);
					contractCode = response.data?.contractCode;

					if (!contractCode) {
						console.error("Contract code not found in response:", response.data);
						toast.error("Failed to get contract details");
						navigate({ to: "/bag" });
						return;
					}

					// Update URL with contractCode so navigating back to this page works
					navigate({
						to: "/checkout/summary",
						search: { contractCode },
						replace: true
					});

					// Store buyer address
					localStorage.setItem("buyerAddress", JSON.stringify({
						address: response.data?.buyerAddress || shippingData.buyerAddress?.address || "",
						city: response.data?.buyerCity || shippingData.buyerAddress?.city || "",
						state: response.data?.buyerState || shippingData.buyerAddress?.state || ""
					}));
					// Store courier pickup office ID for payment page
					const officeId = shippingData.shippingAddress?.courierOfficeId || "";
					if (officeId) {
						setCourierPickupOfficeId(officeId);
						localStorage.setItem("courierPickupOfficeId", officeId);
					}
					toast.success("Order created successfully!");
				}

				// Fetch contract details
				const response = await privateAxios.get(`/contract/view_contract_by_code/${contractCode}`);
				console.log("Contract details response:", response.data);
				const data = response.data?.data;

				// Get buyer address from localStorage
				const storedBuyerAddress = localStorage.getItem("buyerAddress");
				const buyerAddress = storedBuyerAddress ? JSON.parse(storedBuyerAddress) : { address: "", city: "", state: "" };

				setOrderData({
					buyerAddress: {
						address: buyerAddress.address || "",
						city: buyerAddress.city || "",
						state: buyerAddress.state || ""
					},
					vendorAddress: {
						city: data?.shipoutCity || "",
						state: data?.shipoutState || ""
					},
					items: data?.contractItem?.map((item: any) => ({
						id: item.id,
						productId: item.productId?.id || "",
						productName: item.productId?.name || "",
						productPhotos: item.productId?.photos || [],
						quantity: parseInt(item.productQuantity) || 1,
						unitPrice: parseFloat(item.vendorDiscountedPrice) || 0,
						totalPrice: item.totalCost || 0
					})) || [],
					contract: {
						id: data?.id || "",
						contractName: data?.title || "",
						qrCode: data?.contractCode || contractCode,
						pickupLocation: `${data?.shipoutCity}, ${data?.shipoutState}`,
						shipOutDate: data?.shippingDate || ""
					},
					pricing: {
						orderAmount: data?.totalContractValue || 0,
						estimatedCourierFee: parseFloat(data?.deliveryCost) || 0,
						transactionFee: 0,
						providerFee: 0,
						totalAmount: (data?.totalContractValue || 0) + (parseFloat(data?.deliveryCost) || 0)
					}
				});
			}
			catch (error: any) {
				console.error("Order processing error:", error);
				toast.error(error.response?.data?.message || "Failed to load order details");
				navigate({ to: "/bag" });
			}
			finally {
				setIsLoading(false);
			}
		};

		processOrder();
	}, [contractCodeFromSearch, privateAxios, navigate]);

	const handleApplyCoupon = () => {
		if (!couponCode.trim()) {
			toast.error("Please enter a coupon code");
			return;
		}
		// TODO: Implement coupon application
		toast.info("Coupon feature coming soon");
	};

	const handleProceedToPayment = () => {
		// Store payment data for the payment page
		sessionStorage.setItem("paymentPageData", JSON.stringify({
			courierPickupOfficeId,
			orderAmount: orderData?.pricing.totalAmount || 0
		}));
		// Navigate to payment page with contract ID
		navigate({
			to: "/payment/$contractid",
			params: { contractid: orderData?.contract.id || "" }
		});
	};

	if (isLoading) {
		return (
			<div className="bg-gray-50 min-h-screen flex flex-col">
				<NavbarMinimal />
				<div className="flex items-center mb-2">
					<div className="rounded-full p-1" style={{ marginLeft: 12, marginTop: 12 }}>
						<Skeleton className="w-6 h-6 rounded-full" />
					</div>
				</div>
				<div className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
					<div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
						{/* Left Column Skeleton */}
						<div className="space-y-6">
							{/* Shipping Details Skeleton */}
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-40 mb-4" />
								<div className="grid md:grid-cols-2 gap-6">
									<div className="space-y-2">
										<Skeleton className="h-4 w-24 mb-2" />
										<Skeleton className="h-4 w-48" />
										<Skeleton className="h-4 w-36" />
									</div>
									<div className="space-y-2">
										<Skeleton className="h-4 w-28 mb-2" />
										<Skeleton className="h-4 w-44" />
										<Skeleton className="h-4 w-32" />
									</div>
								</div>
								<div className="mt-4 pt-4 border-t">
									<Skeleton className="h-4 w-48" />
								</div>
							</div>

							{/* Order Items Skeleton */}
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-28 mb-4" />
								<div className="space-y-4">
									{[1, 2].map(i => (
										<div key={i} className="flex gap-4">
											<Skeleton className="w-20 h-20 rounded" />
											<div className="flex-1 space-y-2">
												<Skeleton className="h-4 w-40" />
												<Skeleton className="h-4 w-20" />
												<Skeleton className="h-3 w-24" />
											</div>
											<Skeleton className="h-4 w-16" />
										</div>
									))}
								</div>
							</div>

							{/* Order Breakdown Skeleton */}
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-36 mb-4" />
								<div className="space-y-3">
									<div className="flex justify-between">
										<Skeleton className="h-4 w-20" />
										<Skeleton className="h-4 w-24" />
									</div>
									<div className="flex justify-between">
										<Skeleton className="h-4 w-24" />
										<Skeleton className="h-4 w-12" />
									</div>
									<div className="flex justify-between pt-3 border-t">
										<Skeleton className="h-5 w-14" />
										<Skeleton className="h-6 w-28" />
									</div>
								</div>
							</div>
						</div>

						{/* Right Column Skeleton */}
						<div className="space-y-6">
							{/* Contract Invoice Skeleton */}
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-32 mb-4" />
								<div className="flex flex-col items-center space-y-3 mb-4">
									<Skeleton className="h-3 w-24" />
									<Skeleton className="h-5 w-36" />
									<Skeleton className="w-32 h-32 rounded" />
								</div>
								<div className="flex justify-between mt-4">
									<Skeleton className="h-4 w-24" />
									<Skeleton className="h-4 w-28" />
								</div>
							</div>

							{/* Coupon Code Skeleton */}
							<div className="bg-white rounded-lg shadow p-6">
								<Skeleton className="h-6 w-28 mb-4" />
								<div className="flex gap-2">
									<Skeleton className="flex-1 h-10 rounded-md" />
									<Skeleton className="h-10 w-16 rounded-md" />
								</div>
							</div>

							{/* Payment Button Skeleton */}
							<Skeleton className="h-12 w-full rounded-md" />
						</div>
					</div>
				</div>
				<DiscoveryFooter />
			</div>
		);
	}

	if (!orderData) {
		return (
			<div className="bg-gray-50 min-h-screen flex flex-col">
				<NavbarMinimal />
				<div className="flex-1 flex items-center justify-center">
					<div className="text-center py-20">Order not found</div>
				</div>
				<DiscoveryFooter />
			</div>
		);
	}

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			<NavbarMinimal />

			{/* Back button: returns to previous page */}
			<div className="flex items-center mb-2">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "relative", lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, marginLeft: 12, marginTop: 12 }}
					onClick={() => window.history.back()}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>

			<div className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
				<div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
					{/* Left Column */}
					<div className="space-y-6">
						{/* Shipping Details */}
						<div className="bg-white rounded-lg shadow p-6 mb-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Shipping details</h2>
							<div className="grid md:grid-cols-2 gap-6">
								{/* Buyer Address */}
								<div className="space-y-2">
									<h3 className="font-semibold text-sm text-[#2A2A2A] mb-2">Your location</h3>
									<div className="text-sm text-gray-700 space-y-1">
										<div>

											<span className="font-medium">City:</span>
											{" "}
											{orderData.buyerAddress.city || "N/A"}
										</div>
										<div>
											<span className="font-medium">State:</span>
											{" "}
											{orderData.buyerAddress.state || "N/A"}
										</div>
									</div>
								</div>
								{/* Vendor Location */}
								<div className="space-y-2">
									<h3 className="font-semibold text-sm text-[#2A2A2A] mb-2">Vendor location</h3>
									<div className="text-sm text-gray-700 space-y-1">
										<div>
											<span className="font-medium">City:</span>
											{" "}
											{orderData.vendorAddress.city || "N/A"}
										</div>
										<div>
											<span className="font-medium">State:</span>
											{" "}
											{orderData.vendorAddress.state || "N/A"}
										</div>
									</div>
								</div>
							</div>

							{/* Delivery Type - Full width below the two columns */}
							{orderData.buyerAddress.state && orderData.vendorAddress.state && (
								<div className="mt-4 pt-4 border-t">
									<span className="font-medium">Delivery Type:</span>
									{" "}
									<span className={
										orderData.buyerAddress.state === orderData.vendorAddress.state
											? "text-green-600 font-semibold"
											: "text-orange-600 font-semibold"
									}
									>
										{orderData.buyerAddress.state === orderData.vendorAddress.state
											? "Intrastate"
											: "Interstate"}
									</span>
								</div>
							)}
						</div>

						{/* Order Items */}
						<div className="bg-white rounded-lg shadow p-6 mb-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Order items</h2>
							<div className="space-y-4">
								{orderData.items.map(item => (
									<div key={item.id} className="flex gap-4">
										<img
											src={item.productPhotos[0] || "/placeholder.png"}
											alt={item.productName}
											className="w-20 h-20 object-cover rounded"
										/>
										<div className="flex-1">
											<h3 className="text-sm font-medium text-[#2A2A2A]">{item.productName}</h3>
											<div className="text-sm text-gray-600 mt-1">
												₦
												{item.unitPrice.toLocaleString()}
											</div>
											<div className="text-sm text-gray-500 mt-2">
												Quantity:
												{" "}
												{item.quantity}
											</div>
										</div>
										<div className="text-sm font-semibold text-[#2A2A2A]">
											₦
											{item.totalPrice.toLocaleString()}
										</div>
									</div>
								))}
							</div>
						</div>

						{/* Order Breakdown */}
						<div className="bg-white rounded-lg shadow p-6 mb-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Order breakdown</h2>
							<div className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-gray-600">Subtotal</span>
									<span className="font-medium text-[#2A2A2A]">
										₦
										{orderData.pricing.orderAmount.toLocaleString()}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">Delivery fee</span>
									<span className="font-medium text-[#2A2A2A]">TBD</span>
								</div>
								<div className="flex justify-between pt-3 border-t border-gray-200">
									<span className="font-semibold text-gray-700">Total</span>
									<span className="font-bold text-lg text-[#3F2EC8]">
										₦
										{orderData.pricing.totalAmount.toLocaleString()}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Right Column */}
					<div className="space-y-6">
						{/* Contract Invoice */}
						<div className="bg-white rounded-lg shadow p-6 mb-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Contract invoice</h2>

							{/* Contract Code */}
							<div className="text-center mb-2">
								<span className="text-sm text-gray-600">Contract Code</span>
								<div className="text-lg font-semibold text-[#2A2A2A] mt-1">
									{orderData.contract.qrCode}
								</div>
							</div>

							{/* QR Code - Generated from contract code */}
							{orderData.contract.qrCode && (
								<div className="flex justify-center mb-4">
									<QRCode
										value={orderData.contract.qrCode}
										size={128}
										level="M"
									/>
								</div>
							)}

							{/* Ship Out Date */}
							<div className="flex justify-between text-sm mt-4">
								<span className="text-gray-600">Ship out date</span>
								<span className="font-medium text-[#2A2A2A]">
									{new Date(orderData.contract.shipOutDate).toLocaleDateString()}
								</span>
							</div>
						</div>

						{/* Coupon Code */}
						<div className="bg-white rounded-lg shadow p-6 mb-6">
							<h2 className="text-lg font-semibold text-[#2A2A2A] mb-4">Coupon code</h2>
							<div className="flex gap-2">
								<input
									type="text"
									value={couponCode}
									onChange={e => setCouponCode(e.target.value)}
									placeholder="Enter coupon code"
									className="flex-1 px-4 py-2 border border-[#B3B3B3] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F2EC8]"
								/>
								<Button
									onClick={handleApplyCoupon}
									variant="outline"
									className="border-[#3F2EC8] text-[#3F2EC8] hover:bg-[#3F2EC8] hover:text-white"
								>
									Apply
								</Button>
							</div>
						</div>

						{/* Proceed to Payment Button */}
						<Button
							onClick={handleProceedToPayment}
							className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white py-3 text-lg"
						>
							Proceed to payment
						</Button>
					</div>
				</div>
			</div>

			<DiscoveryFooter />
		</div>
	);
}

export const Route = createFileRoute("/checkout/summary")({
	component: OrderSummaryPage,
	validateSearch: (search: Record<string, unknown>): { contractCode?: string } => {
		return {
			contractCode: (search.contractCode as string) || undefined
		};
	}
});
